import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  CircularProgress,
  ClickAwayListener,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import LaunchIcon from "@material-ui/icons/Launch";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import DescriptionIcon from "@material-ui/icons/Description";
import EventNoteIcon from "@material-ui/icons/EventNote";
import MonetizationOnIcon from "@material-ui/icons/MonetizationOn";
import AccountBalanceIcon from "@material-ui/icons/AccountBalance";
import RoomIcon from "@material-ui/icons/Room";
import AssessmentIcon from "@material-ui/icons/Assessment";
import StraightenIcon from "@material-ui/icons/Straighten";
import NotesIcon from "@material-ui/icons/Notes";
import LockIcon from "@material-ui/icons/Lock";
import A_TextField from "../../../../Atoms/Inputs/TextFields/A_TextField";
import A_CurrencyInput from "../../../../Atoms/Inputs/TextFields/A_CurrencyInput";
import A_DatePicker from "../../../../Atoms/Inputs/DateTimePickers/A_DatePicker";
import A_Select from "../../../../Atoms/Inputs/Selects/A_Select";
import A_Button from "../../../../Atoms/Inputs/Buttons/A_Button";
import A_SimpleDialog from "../../../../Atoms/Feedback/Dialogs/A_SimpleDialog";
import LookupSearchSelect from "./LookupSearchSelect";
import DuplicateLoanBanner from "./DuplicateLoanBanner";
import { toEpochFormat } from "../../../../../../Utils/Helpers/dateUtils";
import * as toastActions from "../../../../../../store/actions/toastActions";
import * as currentPageActions from "../../../../../../store/actions/currentPageActions";
import { toastSeverity } from "../../../../../../Utils/config/toastConfig";
import useStyles from "./Add_Loan.styles";
import {
  ALL_FIELDS,
  DEVELOPER_AUTOFILL_EMAILS,
  FEE_GROUPS,
  FEE_MODE_DEFAULTS,
  FEE_MODE_OPTIONS,
  getFieldGridSize,
  isChipSelectField,
  LOAN_STEPS,
  PERCENTAGE_FIELD_KEYS,
} from "./Add_Loan.constants";
import {
  buildDeveloperLoanSampleData,
  buildFormStateFromSource,
  buildLoanErrorItems,
  buildLoanSectionStatuses,
  buildInitialState,
  clearLoanDraft,
  getFieldGroups,
  getFieldHelperText,
  getFieldOptions,
  getLoanDraftStorageKey,
  getRecordSource,
  hasDraftableLoanData,
  hasFieldValue,
  isFieldRequired,
  isNumericType,
  loadBorrowerLookupOptions,
  loadOptionSets,
  loadLoanDraft,
  sanitizeLoanDraft,
  saveLoanDraft,
  shouldShowField,
} from "./Add_Loan.utils";
import { httpPost, httpPut } from "../../../../../../Utils/apis/apis";
import EgSearchField from "./EgSearchField";
import { useSelector } from "react-redux";
import { getCRMContactInfo } from "../../../../../../Utils/Helpers/common";
import { message } from "../../../../../../Utils/config/messages";
import { useParams } from "react-router";
import {
  getLoanSectionLockMessage,
  isLoanSectionLocked,
  LOAN_STAGE_LOCK_MESSAGE,
} from "../../../../Pages/Common/Layouts/layout.Helper";
import A_SecuredAction from "../../../../Atoms/Security/A_SecuredAction";
import {
  FormErrorSummary,
  FormProgressHeader,
  SectionStatusBadge,
  focusField,
  scrollToSection,
  sectionDomId,
} from "../../FormShell";
import { pageSecurityActions } from "../../../../../../Utils/Helpers/pageSecurity";

const addLoanPropTypes = {
  formConfig: PropTypes.shape({
    data: PropTypes.shape({
      BorrowerId: PropTypes.any,
      borrowerId: PropTypes.any,
      _partitionKey: PropTypes.any,
    }),
  }),
  isEdit: PropTypes.bool,
  setToast: PropTypes.func,
  onSaveSuccess: PropTypes.func,
  onClose: PropTypes.func,
  getCurrentPage: PropTypes.func,
};

const addLoanDefaultProps = {
  formConfig: null,
  isEdit: false,
  isEditForm: false,
  setToast: () => {},
  onSaveSuccess: null,
  onClose: null,
  getCurrentPage: null,
};

const CURRENCY_SYMBOLS = {
  GBP: "£",
  USD: "$",
  EUR: "€",
};

const ZERO_DISPLAY_EXCLUDED_FIELDS = ["LoanTerm", "SaleVoidDays"];

const getZeroSafeDisplayValue = (field, value) =>
  value === 0 && !ZERO_DISPLAY_EXCLUDED_FIELDS.includes(field.backendKey)
    ? "0"
    : value;

const SECTION_META = {
  "Loan Details": {
    icon: <DescriptionIcon />,
    subtitle: "Borrower, property and product details",
  },
  "Address Details": {
    icon: <RoomIcon />,
    subtitle: "Property address and location",
  },
  "Loan Terms": {
    icon: <EventNoteIcon />,
    subtitle: "Rates, dates and repayment terms",
  },
  Fees: {
    icon: <MonetizationOnIcon />,
    subtitle: "Arrangement, exit and other fees",
  },
  "Cost Details": {
    icon: <AccountBalanceIcon />,
    subtitle: "Land, construction and professional costs",
  },
  "Pricing Perimeter": {
    icon: <StraightenIcon />,
    subtitle: "Capital rates and RWA pricing inputs",
  },
  "Risk Rating": {
    icon: <AssessmentIcon />,
    subtitle: "Risk status and rating",
  },
  Notes: {
    icon: <NotesIcon />,
    subtitle: "Additional comments and notes",
  },
};

const AddLoanComponent = ({
  formConfig,
  isEdit,
  setToast,
  onSaveSuccess,
  onClose,
  getCurrentPage,
}) => {
  const classes = useStyles();
  const { pageUrl, crmId } = useParams();
  const formCardRef = useRef(null);
  const [activeStep, setActiveStep] = useState(0);
  const [expandedSections, setExpandedSections] = useState(() =>
    LOAN_STEPS.map((step) => step.title)
  );
  const [formData, setFormData] = useState(() => buildInitialState());
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOptionsLoading, setIsOptionsLoading] = useState(true);
  const [optionSetData, setOptionSetData] = useState({});
  const [borrowerOptions, setBorrowerOptions] = useState([]);
  const [borrowerSelection, setBorrowerSelection] = useState(null);
  const [feeModes, setFeeModes] = useState(FEE_MODE_DEFAULTS);
  const [fieldRenderKey, setFieldRenderKey] = useState(0);
  const [availableDraft, setAvailableDraft] = useState(null);
  const [isDraftBootstrapComplete, setIsDraftBootstrapComplete] =
    useState(false);
  const [isDraftSaving, setIsDraftSaving] = useState(false);
  const [searchResults, setSearchResults] = useState({});
  const [isFieldSearching, setIsFieldSearching] = useState({});
  const [isResultsVisible, setIsResultsVisible] = useState({});
  const [showFinishConfirmation, setShowFinishConfirmation] = useState(false);
  const [isPostcodeBannerDismissed, setIsPostcodeBannerDismissed] =
    useState(isEdit);
  const [isAddressLine1BannerDismissed, setIsAddressLine1BannerDismissed] =
    useState(isEdit);
  const [isKeyPrincipalNameBannerDismissed, setIsKeyPrincipalNameBannerDismissed] =
    useState(isEdit);
  const initialFormDataRef = useRef({});
  const effectiveEditMode = isEdit;
  const canUseDrafts = !effectiveEditMode;
  let clientName =
    window?.location?.hostname?.split(".")?.length > 0
      ? window.location.hostname.split(".")[0]
      : "";
  let { crmContactUserEmail } = getCRMContactInfo();
  const LoggedInUserEmail = useSelector((state) =>
    state.getLoggedInUserReducer?.data?.[0]?.properties?.email
      ? state.getLoggedInUserReducer?.data?.[0]?.properties?.email
      : crmContactUserEmail
  );
  const currentPageData = useSelector((state) => state.currentPage?.data);
  const isDeveloperAutofillEnabled =
    clientName === "localhostt" ||
    DEVELOPER_AUTOFILL_EMAILS.includes((LoggedInUserEmail || "").toLowerCase());
  const draftOwnerIdentifier =
    LoggedInUserEmail || crmContactUserEmail || "anonymous";
  const draftStorageKey = canUseDrafts
    ? getLoanDraftStorageKey(draftOwnerIdentifier)
    : null;

  const isSectionLocked = useCallback(
    (sectionTitle) =>
      effectiveEditMode && isLoanSectionLocked(sectionTitle, currentPageData),
    [effectiveEditMode, currentPageData]
  );

  const isFieldLocked = useCallback(
    (field) => isSectionLocked(field?.section),
    [isSectionLocked]
  );

  const isFeeGroupLocked = useCallback(
    (feeModeGroup) =>
      isFieldLocked(
        ALL_FIELDS.find((field) => field.feeModeGroup === feeModeGroup)
      ),
    [isFieldLocked]
  );

  const withLockTooltip = (sectionTitle, node) =>
    isSectionLocked(sectionTitle) ? (
      <Tooltip
        title={getLoanSectionLockMessage(sectionTitle)}
        arrow
        placement="top"
      >
        <span className={classes.lockedFieldWrap}>{node}</span>
      </Tooltip>
    ) : (
      node
    );

  const getVisibleFieldsForStep = useCallback(
    (step, data = formData, modes = feeModes) =>
      step.fields.filter((field) =>
        shouldShowField(field, data, modes, effectiveEditMode)
      ),
    [formData, feeModes, effectiveEditMode]
  );

  const getFieldByKey = useCallback(
    (backendKey) => ALL_FIELDS.find((field) => field.backendKey === backendKey),
    []
  );

  // isSectionLocked is a dep because `locked` is part of each status now;
  // without it a currentPageData change would leave the badges stale.
  const sectionStatuses = useMemo(
    () =>
      buildLoanSectionStatuses(LOAN_STEPS, {
        getVisibleFields: (step) => getVisibleFieldsForStep(step),
        isRequired: (field) =>
          isFieldRequired(field, formData, feeModes, effectiveEditMode),
        formData,
        formErrors,
        isLocked: isSectionLocked,
        sectionMeta: SECTION_META,
      }),
    [
      getVisibleFieldsForStep,
      formData,
      feeModes,
      effectiveEditMode,
      formErrors,
      isSectionLocked,
    ]
  );
  const statusByTitle = useMemo(
    () => Object.fromEntries(sectionStatuses.map((s) => [s.id, s])),
    [sectionStatuses]
  );

  const errorItems = buildLoanErrorItems(LOAN_STEPS, formErrors);

  const isFormDirty = (() => {
    if (!effectiveEditMode) return true;
    const snapshot = initialFormDataRef.current || {};
    const keys = new Set([...Object.keys(snapshot), ...Object.keys(formData)]);
    const normalize = (val) =>
      val === null || val === undefined ? "" : String(val);
    return [...keys].some(
      (key) => normalize(snapshot[key]) !== normalize(formData[key])
    );
  })();

  useEffect(() => {
    let isMounted = true;

    const initializeOptionSets = async () => {
      setIsOptionsLoading(true);
      const [optionSetObject, borrowerLookupOptions] = await Promise.all([
        loadOptionSets(),
        loadBorrowerLookupOptions(),
      ]);
      if (!isMounted) {
        return;
      }
      setOptionSetData(optionSetObject);
      setBorrowerOptions(borrowerLookupOptions);
      setIsOptionsLoading(false);
      setFieldRenderKey((prevState) => prevState + 1);
    };

    initializeOptionSets();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!effectiveEditMode) {
      setFormData(buildInitialState());
      setBorrowerSelection(null);
      setActiveStep(0);
      setFeeModes(FEE_MODE_DEFAULTS);
      return;
    }

    const source = getRecordSource(formConfig);
    const initialData = buildFormStateFromSource(source, optionSetData);
    initialFormDataRef.current = initialData;
    setFormData(initialData);
    setFieldRenderKey((prevState) => prevState + 1);
    setBorrowerSelection(
      source?.BorrowerName
        ? {
            label: source.BorrowerName,
            value: source.BorrowerName,
            borrowerId:
              source?.BorrowerId ||
              source?.borrowerId ||
              source?.BorrowerID ||
              "",
          }
        : null
    );

    setFeeModes((prevState) => {
      const nextState = { ...prevState };

      Object.keys(FEE_MODE_DEFAULTS).forEach((group) => {
        const groupFields = ALL_FIELDS.filter(
          (field) => field.feeModeGroup === group
        );
        const amountField = groupFields.find(
          (field) => field.feeModeValue === "amount"
        );
        const rateField = groupFields.find(
          (field) => field.feeModeValue === "rate"
        );

        const sourceAmountValue = source?.[amountField?.backendKey];
        const sourceRateValue = source?.[rateField?.backendKey];

        if (rateField && hasFieldValue(sourceRateValue)) {
          nextState[group] = "rate";
        } else if (amountField && hasFieldValue(sourceAmountValue)) {
          nextState[group] = "amount";
        }
      });

      return nextState;
    });
  }, [effectiveEditMode, formConfig, optionSetData]);

  useEffect(() => {
    let isMounted = true;

    const fetchDraft = async () => {
      if (!canUseDrafts || !draftStorageKey) {
        if (isMounted) {
          setAvailableDraft(null);
          setIsDraftBootstrapComplete(true);
        }
        return;
      }

      const loadedDraft = await loadLoanDraft(draftStorageKey);
      const draft = sanitizeLoanDraft(loadedDraft);

      if (!isMounted) {
        return;
      }

      setAvailableDraft(
        draft &&
          hasDraftableLoanData(draft.formData, draft.feeModes, draft.activeStep)
          ? draft
          : null
      );
      setIsDraftBootstrapComplete(true);
    };

    fetchDraft();

    return () => {
      isMounted = false;
    };
  }, [canUseDrafts, draftStorageKey]);

  useEffect(() => {
    setFormErrors({});
  }, [activeStep]);

  useEffect(() => {
    if (!effectiveEditMode || !optionSetData || Object.keys(optionSetData).length === 0) {
      return;
    }

    const source = getRecordSource(formConfig);
    const remappedData = buildFormStateFromSource(source, optionSetData);

    const hasChanges = ALL_FIELDS.some(
      (field) => formData[field.backendKey] !== remappedData[field.backendKey]
    );

    if (hasChanges) {
      setFormData(remappedData);
      setFieldRenderKey((prevState) => prevState + 1);
    }
  }, [optionSetData]);

  useEffect(() => {
    const hiddenFieldKeys = ALL_FIELDS.filter(
      (field) => !shouldShowField(field, formData, feeModes, effectiveEditMode)
    ).map((field) => field.backendKey);

    if (hiddenFieldKeys.length === 0) {
      return;
    }

    const hasHiddenValues = hiddenFieldKeys.some((key) =>
      hasFieldValue(formData[key])
    );

    if (!hasHiddenValues) {
      return;
    }

    setFormData((prevState) => {
      const nextState = { ...prevState };

      hiddenFieldKeys.forEach((key) => {
        const field = getFieldByKey(key);

        if (!field || !hasFieldValue(nextState[key])) {
          return;
        }

        nextState[key] = field.type === "file" ? [] : "";
      });

      return nextState;
    });

    setFormErrors((prevState) => {
      const nextErrors = { ...prevState };
      hiddenFieldKeys.forEach((key) => delete nextErrors[key]);
      return nextErrors;
    });
  }, [feeModes, formData]);

  useEffect(() => {
    if (formCardRef.current) {
      formCardRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [activeStep]);

  const validateField = useCallback(
    (field, value, data = formData, modes = feeModes) => {
      const trimmedValue = typeof value === "string" ? value.trim() : value;
      const isEmpty = !hasFieldValue(trimmedValue);

      if (isFieldRequired(field, data, modes, effectiveEditMode) && isEmpty) {
        return message.FIELD_REQUIRED;
      }

      if (isEmpty) {
        return "";
      }

      if (
        (field.type === "int" || field.type === "integer") &&
        !/^-?\d+$/.test(`${trimmedValue}`)
      ) {
        return message.ENTER_WHOLE_NUMBER;
      }

      if (isNumericType(field.type)) {
        const numericValue = Number(trimmedValue);

        if (Number.isNaN(numericValue)) {
          return message.ENTER_VALID_NUMBER;
        }

        if (PERCENTAGE_FIELD_KEYS.includes(field.backendKey)) {
          if (numericValue < 0 || numericValue > 100) {
            return message.ENTER_PERCENTAGE_RANGE;
          }
        }

        const minRuleMatch =
          typeof field.validationRule === "string"
            ? field.validationRule.match(/>=\s*(-?\d+(?:\.\d+)?)/)
            : null;
        if (minRuleMatch) {
          const minValue = Number(minRuleMatch[1]);
          if (!Number.isNaN(minValue) && numericValue < minValue) {
            if (minValue === 0) {
              return message.VALUE_MIN_ZERO;
            }
            if (minValue === 1) {
              return message.VALUE_MIN_ONE;
            }
            return `The value must be ${minValue} or greater.`;
          }
        }
      }

      if (
        field.backendKey === "Currency" &&
        !/^[A-Z]{3}$/.test(`${trimmedValue}`)
      ) {
        return message.CURRENCY_CODE_FORMAT;
      }

      return "";
    },
    [formData, feeModes]
  );

  const validateAllSteps = useCallback(() => {
    const errors = {};

    LOAN_STEPS.forEach((step) => {
      if (isSectionLocked(step.title)) {
        return;
      }
      getVisibleFieldsForStep(step).forEach((field) => {
        const error = validateField(
          field,
          formData[field.backendKey],
          formData,
          feeModes
        );

        if (error) {
          errors[field.backendKey] = error;
        }
      });
    });

    setFormErrors(errors);

    let firstInvalidField;
    let firstInvalidSection;

    LOAN_STEPS.some((step) => {
      const invalidField = getVisibleFieldsForStep(step).find(
        (field) => errors[field.backendKey]
      );

      if (invalidField) {
        firstInvalidField = invalidField.backendKey;
        firstInvalidSection = step.title;
        return true;
      }

      return false;
    });

    return {
      isValid: Object.keys(errors).length === 0,
      firstInvalidField,
      firstInvalidSection,
    };
  }, [
    getVisibleFieldsForStep,
    validateField,
    formData,
    feeModes,
    isSectionLocked,
  ]);

  const handleAccordionToggle = useCallback((sectionTitle) => {
    setExpandedSections((prevState) =>
      prevState.includes(sectionTitle)
        ? prevState.filter((title) => title !== sectionTitle)
        : [...prevState, sectionTitle]
    );
  }, []);

  const ensureSectionExpanded = useCallback((sectionTitle) => {
    setExpandedSections((prevState) =>
      prevState.includes(sectionTitle)
        ? prevState
        : [...prevState, sectionTitle]
    );
  }, []);

  const dismissAvailableDraft = useCallback(() => {
    if (availableDraft) {
      setAvailableDraft(null);
    }
  }, [availableDraft]);

  const updateFieldValue = useCallback(
    (id, value) => {
      if (isFieldLocked(getFieldByKey(id))) {
        return;
      }
      dismissAvailableDraft();
      if (id === "Postcode") {
        setIsPostcodeBannerDismissed(false);
        setSearchResults((prev) => ({ ...prev, Postcode: [] }));
      }
      if (id === "AddressLine1") {
        setIsAddressLine1BannerDismissed(false);
        setSearchResults((prev) => ({ ...prev, AddressLine1: [] }));
      }
      if (id === "KeyPrincipalName") {
        setIsKeyPrincipalNameBannerDismissed(false);
        setSearchResults((prev) => ({ ...prev, KeyPrincipalName: [] }));
      }
      setFormData((prevData) => ({
        ...prevData,
        [id]: value,
      }));
      setFormErrors((prevState) => {
        if (!prevState[id]) {
          return prevState;
        }
        const nextErrors = { ...prevState };
        delete nextErrors[id];
        return nextErrors;
      });
    },
    [dismissAvailableDraft, getFieldByKey, isFieldLocked]
  );

  const getFieldResetValue = useCallback(
    (field) =>
      field?.defaultValue !== undefined
        ? field.defaultValue
        : field?.type === "file"
        ? []
        : "",
    []
  );

  // The other field of a rate/amount pair (e.g. ContingencyPercent ↔ ContingencyAmount).
  const getFeeModeSibling = useCallback(
    (backendKey) => {
      const field = getFieldByKey(backendKey);
      if (!field?.feeModeGroup || !field.feeModeValue) {
        return null;
      }
      return (
        ALL_FIELDS.find(
          (item) =>
            item.feeModeGroup === field.feeModeGroup &&
            item.feeModeValue &&
            item.backendKey !== backendKey
        ) || null
      );
    },
    [getFieldByKey]
  );

  const resetFeeModeSibling = useCallback(
    (backendKey) => {
      const sibling = getFeeModeSibling(backendKey);

      if (!sibling) {
        return;
      }

      const resetValue = getFieldResetValue(sibling);

      setFormData((prevData) =>
        `${prevData[sibling.backendKey]}` === `${resetValue}`
          ? prevData
          : { ...prevData, [sibling.backendKey]: resetValue }
      );
      setFormErrors((prevState) => {
        if (!prevState[sibling.backendKey]) {
          return prevState;
        }
        const nextErrors = { ...prevState };
        delete nextErrors[sibling.backendKey];
        return nextErrors;
      });
    },
    [getFeeModeSibling, getFieldResetValue]
  );

  const handleTextChange = (event) => {
    const { id, value } = event.target;
    updateFieldValue(id, value);
    resetFeeModeSibling(id);
  };

  const handlePercentageChange = useCallback(
    (event) => {
      const { id, value } = event.target;
      if (value !== "" && value !== "-" && value !== ".") {
        const num = parseFloat(value);
        if (!isNaN(num) && (num < 0 || num > 100)) return;
      }
      updateFieldValue(id, value);
      resetFeeModeSibling(id);
    },
    [updateFieldValue, resetFeeModeSibling]
  );

  const handleDateChange = (value, id) => {
    updateFieldValue(id, value === 0 ? "" : value);
  };

  const handleSelectChange = (selectedOption, id) => {
    updateFieldValue(id, selectedOption?.value || "");
  };

  const handleBooleanChange = (event) => {
    const { name, value } = event.target;
    updateFieldValue(name, value);
  };

  const handleLookupChange = (selected, id) => {
    if (Array.isArray(selected)) {
      updateFieldValue(
        id,
        selected.map((o) => o?.value).filter((v) => v !== undefined)
      );
      return;
    }

    if (id === "BorrowerName") {
      setBorrowerSelection(
        selected
          ? {
              label: selected.label || selected.value || "",
              value: selected.value || selected.label || "",
              borrowerId: selected.borrowerId || "",
            }
          : null
      );
      updateFieldValue(id, selected?.label || selected?.value || "");
      return;
    }

    updateFieldValue(id, selected?.value || "");
  };

  const handleFieldSearch = async (fieldId, value, isExplicit = false) => {
    const trimmedValue = value?.trim() || "";
    if (isFieldSearching[fieldId]) return;
    if (!trimmedValue || trimmedValue.length < 2) {
      setSearchResults((prev) => ({ ...prev, [fieldId]: [] }));
      return;
    }

    try {
      setIsFieldSearching((prev) => ({ ...prev, [fieldId]: true }));
      let spName, parameters;

      if (fieldId === "AddressLine1") {
        spName = "DF.usp_PrefilledLoanDetailsByAddressLine1";
        parameters = { addressLine1: value.trim() };
      } else if (fieldId === "KeyPrincipalName") {
        spName = "DF.usp_PrefilledLoanDetailsByKeyPrincipalName";
        parameters = { keyPrincipalName: value.trim() };
      } else {
        spName = "DF.usp_PrefilledLoanDetailsByPostcode";
        parameters = { postCode: value.trim() };
      }

      const payload = [
        {
          id: "Search_Results",
          spName,
          parameters,
        },
      ];
      const response = await httpPost(
        "/SQLQuery/getDataFromMultipleSPs?chartSPs=false",
        payload
      );
      const data = response?.Search_Results || [];

      setSearchResults((prev) => ({ ...prev, [fieldId]: data }));

      if (data.length === 0 && isExplicit) {
        let toastMessage = message.NO_POSTCODE_RESULTS;
        if (fieldId === "AddressLine1") {
          toastMessage = message.NO_ADDRESS_LINE1_RESULTS;
        } else if (fieldId === "KeyPrincipalName") {
          toastMessage = message.NO_KEY_PRINCIPAL_NAME_RESULTS;
        }
        setToast({
          type: toastSeverity.INFO,
          severity: toastSeverity.INFO,
          message: toastMessage,
          status: true,
          duration: 3000,
        });
      }
    } catch (error) {
      setToast({
        type: toastSeverity.ERROR,
        severity: toastSeverity.ERROR,
        message: message.SEARCH_FAILED,
        status: true,
        duration: 3000,
      });
    } finally {
      setIsFieldSearching((prev) => ({ ...prev, [fieldId]: false }));
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      ALL_FIELDS.forEach((field) => {
        if (field.type === "search-string" && formData[field.backendKey]) {
          handleFieldSearch(field.backendKey, formData[field.backendKey]);
        }
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.Postcode, formData.AddressLine1, formData.KeyPrincipalName]);

  const toggleResultsVisibility = (fieldId, visible) => {
    setIsResultsVisible((prev) => ({ ...prev, [fieldId]: visible }));
  };

  const handleFeeModeChange = (group, value) => {
    if (isFeeGroupLocked(group)) {
      return;
    }
    dismissAvailableDraft();
    if (!effectiveEditMode) {
      const groupFields = ALL_FIELDS.filter(
        (field) => field.feeModeGroup === group
      );
      groupFields.forEach((field) => {
        if (field.feeModeValue && field.feeModeValue !== value) {
          // Reset to the field's default (0) rather than empty, so the side you switch
          // away from reads 0 when you come back to it.
          updateFieldValue(field.backendKey, getFieldResetValue(field));
        }
      });
    }

    setFeeModes((prevState) => ({
      ...prevState,
      [group]: value,
    }));
  };

  const handleRestoreDraft = useCallback(() => {
    if (!availableDraft) {
      return;
    }

    const restoredData = availableDraft.formData;
    const restoredModes = {
      ...FEE_MODE_DEFAULTS,
      ...availableDraft.feeModes,
    };

    setFormData(restoredData);
    setFeeModes(restoredModes);
    setBorrowerSelection(availableDraft.borrowerSelection || null);
    setActiveStep(
      Math.min(
        Math.max(availableDraft.activeStep || 0, 0),
        LOAN_STEPS.length - 1
      )
    );
    setFieldRenderKey((prevState) => prevState + 1);

    const restoredErrors = {};
    LOAN_STEPS.forEach((step) => {
      step.fields
        .filter((field) =>
          shouldShowField(field, restoredData, restoredModes, effectiveEditMode)
        )
        .forEach((field) => {
          const error = validateField(
            field,
            restoredData[field.backendKey],
            restoredData,
            restoredModes
          );
          if (error) {
            restoredErrors[field.backendKey] = error;
          }
        });
    });
    setFormErrors(restoredErrors);

    const postcode = availableDraft.formData?.Postcode;
    if (postcode && postcode.trim().length >= 2) {
      handleFieldSearch("Postcode", postcode);
    }

    const addressLine1 = availableDraft.formData?.AddressLine1;
    if (addressLine1 && addressLine1.trim().length >= 2) {
      handleFieldSearch("AddressLine1", addressLine1);
    }

    const keyPrincipalName = availableDraft.formData?.KeyPrincipalName;
    if (keyPrincipalName && keyPrincipalName.trim().length >= 2) {
      handleFieldSearch("KeyPrincipalName", keyPrincipalName);
    }

    setAvailableDraft(null);
    setToast({
      type: toastSeverity.SUCCESS,
      severity: toastSeverity.SUCCESS,
      message: message.LOAN_DRAFT_RESTORED,
      status: true,
      duration: 3000,
    });
  }, [availableDraft, setToast, validateField, effectiveEditMode]);

  const handleDiscardDraft = useCallback(async () => {
    if (draftStorageKey) {
      await clearLoanDraft(draftStorageKey);
    }

    setAvailableDraft(null);
    setFormData(buildInitialState());
    setBorrowerSelection(null);
    setFeeModes(FEE_MODE_DEFAULTS);
    setActiveStep(0);
    setFormErrors({});
    setSearchResults({});
    setIsFieldSearching({});
    setIsResultsVisible({});
    setFieldRenderKey((prevState) => prevState + 1);
    setToast({
      type: toastSeverity.SUCCESS,
      severity: toastSeverity.SUCCESS,
      message: message.LOAN_DRAFT_DISCARDED,
      status: true,
      duration: 3000,
    });
  }, [draftStorageKey, setToast]);

  const saveDraft = useCallback(
    async ({
      showEmptyWarning = true,
      successMessage = message.LOAN_DRAFT_SAVED,
    } = {}) => {
      if (
        !canUseDrafts ||
        !draftStorageKey ||
        !isDraftBootstrapComplete ||
        isDraftSaving
      ) {
        return { saved: false, skipped: true };
      }

      if (!hasDraftableLoanData(formData, feeModes, activeStep)) {
        if (showEmptyWarning) {
          setToast({
            type: toastSeverity.ERROR,
            severity: toastSeverity.ERROR,
            message: message.LOAN_DRAFT_EMPTY_WARNING,
            status: true,
            duration: 3000,
          });
        }
        return { saved: false, skipped: true, empty: true };
      }

      setIsDraftSaving(true);

      const saved = await saveLoanDraft(draftStorageKey, {
        version: 1,
        activeStep,
        feeModes,
        formData,
        borrowerSelection,
        updatedAt: new Date().toISOString(),
      });

      setIsDraftSaving(false);

      setToast({
        type: saved ? toastSeverity.SUCCESS : toastSeverity.ERROR,
        severity: saved ? toastSeverity.SUCCESS : toastSeverity.ERROR,
        message: saved ? successMessage : message.SOMETHING_WENT_WRONG,
        status: true,
        duration: saved ? 3000 : 5000,
      });

      if (saved) {
        setAvailableDraft(null);
      }

      return { saved, skipped: false };
    },
    [
      canUseDrafts,
      draftStorageKey,
      isDraftBootstrapComplete,
      isDraftSaving,
      formData,
      feeModes,
      activeStep,
      borrowerSelection,
      setToast,
    ]
  );

  const handleSaveDraft = useCallback(() => saveDraft(), [saveDraft]);

  const handleDeveloperRandomFill = useCallback(() => {
    const sampleData = buildDeveloperLoanSampleData(
      formData,
      optionSetData,
      feeModes,
      borrowerOptions
    );
    const autofillBorrower = borrowerOptions[0] || null;

    dismissAvailableDraft();
    setFormData(sampleData.formData);
    setBorrowerSelection(
      autofillBorrower
        ? {
            label: autofillBorrower.label || autofillBorrower.value || "",
            value: autofillBorrower.value || autofillBorrower.label || "",
            borrowerId: autofillBorrower.borrowerId || "",
          }
        : null
    );
    setFeeModes(sampleData.feeModes);
    setFormErrors({});
    setFieldRenderKey((prevState) => prevState + 1);
  }, [
    borrowerOptions,
    dismissAvailableDraft,
    feeModes,
    formData,
    optionSetData,
  ]);

  const handleFinish = () => {
    const validationResult = validateAllSteps();

    if (!validationResult.isValid) {
      if (validationResult.firstInvalidSection) {
        ensureSectionExpanded(validationResult.firstInvalidSection);
      }
      if (validationResult.firstInvalidField) {
        focusField(validationResult.firstInvalidField);
      }
      return;
    }

    setShowFinishConfirmation(true);
  };

  const executeSubmission = useCallback(async () => {
    setShowFinishConfirmation(false);

    const properties = ALL_FIELDS.reduce((result, field) => {
      let value = formData[field.backendKey];
      if (
        field.feeModeGroup &&
        field.feeModeValue &&
        feeModes[field.feeModeGroup] !== field.feeModeValue
      ) {
        result[field.backendKey] = "";
        return result;
      }

      const isVisible = shouldShowField(
        field,
        formData,
        feeModes,
        effectiveEditMode
      );

      if (!isVisible) {
        result[field.backendKey] = field.type === "file" ? [] : "";
        return result;
      }

      if (value === null || value === undefined || (value === "" && field.defaultValue !== undefined)) {
        value = field.defaultValue !== undefined && value === "" ? field.defaultValue : (field.type === "file" ? [] : "");
      }

      if (field.type === "date" || field.type === "datetime") {
        if (value && value !== "") {
          let dateObj = value;
          if (typeof value === "string") {
            dateObj = new Date(value);
          } else if (typeof value === "number" && value < 10000000000) {
            dateObj = new Date(value * 1000);
          }
          result[field.backendKey] = toEpochFormat(dateObj);
        } else {
          result[field.backendKey] = value;
        }
        return result;
      }

      if (field.type === "optionset") {
        result[field.backendKey] = value || "";
      } else {
        result[field.backendKey] = value;
      }

      return result;
    }, {});
    properties.BorrowerName = formData.BorrowerName || "";
    properties.BorrowerId =
      borrowerSelection?.borrowerId ||
      formConfig?.data?.BorrowerId ||
      formConfig?.data?.borrowerId ||
      "";

    setIsSubmitting(true);
    properties.CreatedBy = LoggedInUserEmail;
    properties.statusPipeline = "pe-deals-workflow-config";
    properties.currentStatus = "Enquiry";

    const insertPayload = {
      id: effectiveEditMode
        ? formConfig?.data?._partitionKey ||
          formData.ApplicationId ||
          crmId ||
          null
        : null,
      label: "loan",
      type: "vertex",
      properties,
      edges: [],
      security: {
        read: { groupids: [], users: [] },
        update: { groupids: [], users: [] },
        full: { groupids: [], users: [] },
        privateRecord: true,
      },
      duplicateDetection: null,
      submitToStoredProc: effectiveEditMode
        ? "DF.usp_EditLoan"
        : "DF.usp_AddLoan",
    };

    try {
      const response = effectiveEditMode
        ? await httpPut(
            "/HTBCRMData/update?validationType=loan",
            insertPayload,
            {},
            true
          )
        : await httpPost(
            "/HTBCRMData/insert?validationType=loan",
            insertPayload,
            {},
            true
          );
      if (response?.data?.id != null) {
        if (canUseDrafts && draftStorageKey) {
          await clearLoanDraft(draftStorageKey);
        }
        setToast({
          type: toastSeverity.SUCCESS,
          severity: toastSeverity.SUCCESS,
          message: effectiveEditMode ? message.LOAN_UPDATED : message.LOAN_SAVED,
          status: true,
          duration: 4000,
        });
        if (effectiveEditMode) {
          if (pageUrl && crmId && getCurrentPage) {
            getCurrentPage({ pageUrl, crmId });
          }
          if (onClose) {
            onClose();
          }
        } else if (onSaveSuccess) {
          onSaveSuccess(response.data.id);
        }
        return;
      }

      const errorMessage =
        response?.message ||
        response?.response?.data?.message ||
        message.SOMETHING_WENT_WRONG;

      setToast({
        type: toastSeverity.ERROR,
        severity: toastSeverity.ERROR,
        message: errorMessage,
        status: true,
        duration: 5000,
      });
    } catch (error) {
      setToast({
        type: toastSeverity.ERROR,
        severity: toastSeverity.ERROR,
        message: message.SOMETHING_WENT_WRONG,
        status: true,
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    formData,
    borrowerSelection,
    formConfig,
    effectiveEditMode,
    canUseDrafts,
    draftStorageKey,
    setToast,
    onSaveSuccess,
    onClose,
    getCurrentPage,
    pageUrl,
    crmId,
    LoggedInUserEmail,
    feeModes,
  ]);

  const renderChipSelect = (field) => {
    const locked = isFieldLocked(field);
    const required = isFieldRequired(
      field,
      formData,
      feeModes,
      effectiveEditMode
    );
    const options =
      field.type === "boolean"
        ? field.fixedOptions || []
        : getFieldOptions(field, formData[field.backendKey], optionSetData) ||
          [];
    const currentValue = formData[field.backendKey];
    const errorText = formErrors[field.backendKey];
    const helperText = getFieldHelperText(field);

    return (
      <div
        className={classes.chipSelectWrap}
        id={field.backendKey}
        key={`${field.backendKey}-${fieldRenderKey}`}
      >
        <Typography className={classes.chipSelectLabel}>
          {field.label}
          {required ? (
            <span className={classes.chipSelectRequired}> *</span>
          ) : null}
        </Typography>
        <div className={classes.chipSelectOptions}>
          {options.map((option) => {
            const optionValue = `${option.value}`;
            const selected = `${currentValue}` === optionValue;
            return (
              <Chip
                key={`${field.backendKey}-${optionValue}`}
                label={option.label}
                size="small"
                clickable={!locked}
                disabled={locked}
                onClick={() =>
                  updateFieldValue(
                    field.backendKey,
                    selected && !required ? "" : option.value
                  )
                }
                className={`${classes.selectChip} ${
                  selected ? classes.selectChipActive : ""
                }`}
              />
            );
          })}
        </div>
        {errorText || helperText ? (
          <Typography
            className={classes.chipSelectHelper}
            style={errorText ? { color: "#f44336" } : undefined}
          >
            {errorText || helperText}
          </Typography>
        ) : null}
      </div>
    );
  };

  const renderFieldControl = (field) => {
    const locked = isFieldLocked(field);
    if (isChipSelectField(field)) {
      return renderChipSelect(field);
    }
    if (field.type === "lookup") {
      const options =
        field.backendKey === "BorrowerName"
          ? borrowerOptions
          : getFieldOptions(field, formData[field.backendKey], optionSetData) ||
            [];

      return (
        <LookupSearchSelect
          key={`${field.backendKey}-${fieldRenderKey}-${options.length}`}
          id={field.backendKey}
          label={field.label}
          fullWidth
          required={isFieldRequired(
            field,
            formData,
            feeModes,
            effectiveEditMode
          )}
          defaultValue={formData[field.backendKey]}
          options={options}
          multiple={!!field.multiple}
          disabled={locked}
          onChange={handleLookupChange}
          fieldHelperText={getFieldHelperText(field)}
          error={!!formErrors[field.backendKey]}
          helperText={formErrors[field.backendKey]}
        />
      );
    }

    if (field.type === "optionset") {
      const options = getFieldOptions(
        field,
        formData[field.backendKey],
        optionSetData
      );

      return (
        <A_Select
          key={`${field.backendKey}-${fieldRenderKey}-${options.length}`}
          id={field.backendKey}
          label={field.label}
          fullWidth
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          required={isFieldRequired(
            field,
            formData,
            feeModes,
            effectiveEditMode
          )}
          defaultValue={formData[field.backendKey]}
          options={options}
          disabled={locked}
          onChange={handleSelectChange}
          fieldHelperText={getFieldHelperText(field)}
          error={!!formErrors[field.backendKey]}
          helperText={formErrors[field.backendKey]}
        />
      );
    }
    if (field.type === "boolean") {
      const helperText =
        formErrors[field.backendKey] || getFieldHelperText(field);

      return (
        <FormControl
          key={`${field.backendKey}-${fieldRenderKey}`}
          component="fieldset"
          fullWidth
          error={!!formErrors[field.backendKey]}
          className={classes.booleanFieldWrap}
        >
          <Typography className={classes.booleanFieldLabel}>
            {field.label}
          </Typography>
          <RadioGroup
            row
            name={field.backendKey}
            value={formData[field.backendKey]}
            onChange={handleBooleanChange}
            className={classes.radioGroup}
          >
            {(field.fixedOptions || []).map((option) => (
              <FormControlLabel
                key={`${field.backendKey}-${String(option.value)}`}
                value={String(option.value)}
                disabled={locked}
                control={<Radio color="primary" size="small" />}
                label={option.label}
              />
            ))}
          </RadioGroup>
          {helperText ? (
            <Typography
              className={classes.booleanFieldHelper}
              style={
                formErrors[field.backendKey] ? { color: "#f44336" } : undefined
              }
            >
              {helperText}
            </Typography>
          ) : null}
        </FormControl>
      );
    }
    if (field.type === "search-string") {
      let fieldResults = searchResults[field.backendKey] || [];
      const isSelfReferentialOnly =
        effectiveEditMode &&
        fieldResults.length === 1 &&
        String(fieldResults[0]["Loan Id"]) === String(crmId);

      if (effectiveEditMode) {
        fieldResults = fieldResults.filter(
          (res) => String(res["Loan Id"]) !== String(crmId)
        );
      }

      return (
        <div className={classes.searchResultContainer}>
          <A_TextField
            key={`${field.backendKey}-${fieldRenderKey}`}
            id={field.backendKey}
            name={field.backendKey}
            label={field.label}
            variant="outlined"
            fullWidth
            InputLabelProps={{ shrink: true }}
            defaultValue={formData[field.backendKey] || ""}
            inputType="value"
            disabled={locked}
            onChange={handleTextChange}
            onBlur={() => {
              setTimeout(() => {
                toggleResultsVisibility(field.backendKey, false);
              }, 200);
            }}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleFieldSearch(
                  field.backendKey,
                  formData[field.backendKey],
                  true
                );
                toggleResultsVisibility(field.backendKey, true);
              }
            }}
            required={isFieldRequired(
              field,
              formData,
              feeModes,
              effectiveEditMode
            )}
            error={!!formErrors[field.backendKey]}
            helperText={formErrors[field.backendKey]}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    fontSize="small"
                    className={classes.searchStartIcon}
                    style={
                      formData[field.backendKey]?.trim()?.length >= 2
                        ? { cursor: "pointer" }
                        : undefined
                    }
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => {
                      if (!(formData[field.backendKey]?.trim()?.length >= 2)) {
                        return;
                      }
                      e.stopPropagation();
                      handleFieldSearch(
                        field.backendKey,
                        formData[field.backendKey],
                        true
                      );
                      toggleResultsVisibility(field.backendKey, true);
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment: isFieldSearching[field.backendKey] ? (
                <InputAdornment position="end">
                  <CircularProgress
                    size={18}
                    className={classes.searchingSpinner}
                  />
                </InputAdornment>
              ) : null,
            }}
          />
          {isResultsVisible[field.backendKey] && !isSelfReferentialOnly && (
            <ClickAwayListener
              onClickAway={() =>
                toggleResultsVisibility(field.backendKey, false)
              }
            >
              <Paper className={classes.searchResultPaper} elevation={3}>
                {isFieldSearching[field.backendKey] &&
                fieldResults.length === 0 ? (
                  <div className={classes.searchResultLoading}>
                    <CircularProgress size={16} />
                    <Typography variant="caption">Searching...</Typography>
                  </div>
                ) : fieldResults.length > 0 ? (
                  <>
                    <Typography className={classes.searchResultHeader}>
                      {field.backendKey === "KeyPrincipalName"
                        ? "Existing Loans for this Key Principal"
                        : field.backendKey === "AddressLine1"
                        ? "Existing Loans for this Address"
                        : "Existing Loans for this Postcode"}
                    </Typography>
                    {fieldResults.map((res, index) => (
                      <div
                        key={index}
                        className={classes.searchResultItem}
                        onClick={() =>
                          window.open(
                            `/loan/${res["Loan Id"]}/ac4f4d6d-6477-4cb8-ba02-c27b065d08fc`,
                            "_blank"
                          )
                        }
                      >
                        <div className={classes.searchResultInfo}>
                          <Typography className={classes.searchResultTitle}>
                            {res["Loan Name"] || "Unnamed Loan"}
                          </Typography>
                          <Typography className={classes.searchResultSubTitle}>
                            {res["Borrower Name"] || "Unknown Entity"} •{" "}
                            {res["Loan Status"] || "No status available"}
                          </Typography>
                        </div>
                        <div className={classes.searchResultAction}>
                          <LaunchIcon style={{ fontSize: "1rem" }} />
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <div className={classes.searchResultEmpty}>
                    <Typography variant="caption">
                      No applications found.
                    </Typography>
                  </div>
                )}
              </Paper>
            </ClickAwayListener>
          )}
        </div>
      );
    }

    if (field.type === "exposure-group-search") {
      return (
        <EgSearchField
          key={`${field.backendKey}-${fieldRenderKey}`}
          id={field.backendKey}
          label={field.label}
          value={formData[field.backendKey] || ""}
          onChange={(val) => updateFieldValue(field.backendKey, val)}
          required={isFieldRequired(field, formData, feeModes, effectiveEditMode)}
          error={!!formErrors[field.backendKey]}
          helperText={formErrors[field.backendKey]}
          fetchUrl="/ExposureGroup/getall"
          createUrl="/exposuregroup/addgroup"
          createPayloadKey="name"
          displayKey="name"
        />
      );
    }

    if (field.type === "date" || field.type === "datetime") {
      return (
        <A_DatePicker
          key={`${field.backendKey}-${fieldRenderKey}`}
          id={field.backendKey}
          label={field.label}
          fullWidth
          margin="dense"
          placeholder="DD/MM/YYYY"
          inputVariant="outlined"
          InputLabelProps={{ shrink: true }}
          required={isFieldRequired(
            field,
            formData,
            feeModes,
            effectiveEditMode
          )}
          defaultValue={formData[field.backendKey] || null}
          disabled={locked}
          onChange={handleDateChange}
          // fieldHelperText={getFieldHelperText(field)}
          error={!!formErrors[field.backendKey]}
          helperText={formErrors[field.backendKey]}
        />
      );
    }

    const isCurrencyAmountField =
      (field.type === "float" ||
        field.type === "decimal" ||
        field.helperText === "GBP") &&
      !field.multiline &&
      !PERCENTAGE_FIELD_KEYS.includes(field.backendKey) &&
      !field.backendKey.endsWith("Rate");

    if (isCurrencyAmountField) {
      const currencyValue = formData[field.backendKey];
      return (
        <A_CurrencyInput
          key={`${field.backendKey}-${fieldRenderKey}`}
          id={field.backendKey}
          name={field.backendKey}
          label={field.label}
          variant="outlined"
          fullWidth
          required={isFieldRequired(field, formData, feeModes, effectiveEditMode)}
          disabled={field.disabled || locked}
          readOnly={field.readOnly}
          InputLabelProps={{ shrink: true }}
          value={currencyValue}
          onChange={handleTextChange}
          InputProps={
            field.disabled || locked
              ? {
                  endAdornment: (
                    <LockIcon fontSize="small" className={classes.lockIcon} />
                  ),
                }
              : undefined
          }
          currencySymbol={
            CURRENCY_SYMBOLS[formData.Currency] || formData.Currency || "£"
          }
          fieldHelperText={getFieldHelperText(field)}
          error={!!formErrors[field.backendKey]}
          helperText={formErrors[field.backendKey]}
        />
      );
    }

    const fieldValue = formData[field.backendKey];
    const displayValue = getZeroSafeDisplayValue(field, fieldValue);

    return (
      <A_TextField
        key={`${field.backendKey}-${fieldRenderKey}`}
        id={field.backendKey}
        name={field.backendKey}
        label={field.label}
        variant="outlined"
        type={isNumericType(field.type) ? "number" : undefined}
        fullWidth
        required={isFieldRequired(field, formData, feeModes, effectiveEditMode)}
        disabled={field.disabled || locked}
        readOnly={field.readOnly}
        InputLabelProps={{ shrink: true }}
        defaultValue={displayValue}
        inputType="value"
        multiline={field.multiline}
        rows={field.rows}
        onChange={
          PERCENTAGE_FIELD_KEYS.includes(field.backendKey) ||
          field.backendKey.endsWith("Rate")
            ? handlePercentageChange
            : handleTextChange
        }
        inputProps={
          PERCENTAGE_FIELD_KEYS.includes(field.backendKey) ||
          field.backendKey.endsWith("Rate")
            ? { min: 0, max: 100, step: "any" }
            : {}
        }
        InputProps={
          field.disabled || locked
            ? {
                endAdornment: (
                  <LockIcon fontSize="small" className={classes.lockIcon} />
                ),
              }
            : undefined
        }
        adornment={
          PERCENTAGE_FIELD_KEYS.includes(field.backendKey) ||
          field.backendKey.endsWith("Rate")
            ? { position: "end", value: "%" }
            : undefined
        }
        fieldHelperText={getFieldHelperText(field)}
        error={!!formErrors[field.backendKey]}
        helperText={formErrors[field.backendKey]}
      />
    );
  };

  const renderField = (field) =>
    withLockTooltip(field.section, renderFieldControl(field));

  const renderFieldGrid = (field) => {
    const { xs, sm, md } = getFieldGridSize(field);
    return (
      <Grid item xs={xs} sm={sm} md={md} key={field.backendKey}>
        {renderField(field)}
      </Grid>
    );
  };

  const renderStandardStep = (step) => {
    const locked = isSectionLocked(step.title);
    const visibleFields = getVisibleFieldsForStep(step);
    const groups = getFieldGroups(visibleFields);
    const renderedFeeGroups = new Set();

    return groups.map((group) => (
      <div key={group.title || "default"} className={classes.subSectionWrap}>
        {group.title ? (
          <Typography className={classes.subSectionTitle}>
            {group.title}
          </Typography>
        ) : null}
        <Grid container spacing={1}>
          {group.items.map((field) => {
            if (field.feeModeGroup && field.feeModeValue) {
              if (renderedFeeGroups.has(field.feeModeGroup)) {
                return null;
              }
              renderedFeeGroups.add(field.feeModeGroup);

              const rateField = group.items.find(
                (f) =>
                  f.feeModeGroup === field.feeModeGroup &&
                  f.feeModeValue === "rate"
              );
              const amountField = group.items.find(
                (f) =>
                  f.feeModeGroup === field.feeModeGroup &&
                  f.feeModeValue === "amount"
              );

              if (rateField && amountField) {
                const activeField =
                  feeModes[field.feeModeGroup] === "amount"
                    ? amountField
                    : rateField;

                return (
                  <Grid
                    item
                    xs={12}
                    sm={activeField.fullWidth || activeField.multiline ? 12 : 6}
                    md={activeField.fullWidth || activeField.multiline ? 12 : 4}
                    key={field.feeModeGroup}
                  >
                    {withLockTooltip(
                      step.title,
                      <div
                        className={classes.feeModeWrap}
                        style={{ marginBottom: 4 }}
                      >
                        <Typography className={classes.feeModeLabel}>
                          Enter as
                        </Typography>
                        <FormControl component="fieldset">
                          <RadioGroup
                            className={classes.radioGroup}
                            value={feeModes[field.feeModeGroup]}
                            onChange={(event) =>
                              handleFeeModeChange(
                                field.feeModeGroup,
                                event.target.value
                              )
                            }
                          >
                            {FEE_MODE_OPTIONS.map((option) => (
                              <FormControlLabel
                                key={option.value}
                                value={option.value}
                                disabled={locked}
                                control={
                                  <Radio color="primary" size="small" />
                                }
                                label={option.label}
                              />
                            ))}
                          </RadioGroup>
                        </FormControl>
                      </div>
                    )}
                    {renderField(activeField)}
                  </Grid>
                );
              }
            }
            return renderFieldGrid(field);
          })}
        </Grid>
      </div>
    ));
  };

  const renderFeesStep = (step) => {
    const locked = isSectionLocked(step.title);
    const nonFeeFields = step.fields.filter((field) => !field.feeModeGroup);

    return (
      <Grid container spacing={1}>
        {FEE_GROUPS.map((group) => {
          const rateField = step.fields.find(
            (field) =>
              field.feeModeGroup === group.key && field.feeModeValue === "rate"
          );
          const amountField = step.fields.find(
            (field) =>
              field.feeModeGroup === group.key &&
              field.feeModeValue === "amount"
          );
          const relatedFields = step.fields.filter(
            (field) => field.feeModeGroup === group.key && !field.feeModeValue
          );
          const activeValueField =
            (feeModes[group.key] === "amount" ? amountField : rateField) ||
            amountField ||
            rateField;

          if (!rateField && !amountField && relatedFields.length === 0) {
            return null;
          }

          return (
            <Grid item xs={12} sm={6} md={4} key={group.key}>
              <div className={classes.feeCard}>
                <Typography className={classes.feeCardTitle}>
                  {group.title}
                </Typography>
                {rateField &&
                  amountField &&
                  withLockTooltip(
                    step.title,
                    <div className={classes.feeModeWrap}>
                      <Typography className={classes.feeModeLabel}>
                        Enter as
                      </Typography>
                      <FormControl component="fieldset">
                        <RadioGroup
                          className={classes.radioGroup}
                          value={feeModes[group.key]}
                          onChange={(event) =>
                            handleFeeModeChange(group.key, event.target.value)
                          }
                        >
                          {FEE_MODE_OPTIONS.map((option) => (
                            <FormControlLabel
                              key={option.value}
                              value={option.value}
                              disabled={locked}
                              control={<Radio color="primary" size="small" />}
                              label={option.label}
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>
                    </div>
                  )}

                {activeValueField ? (
                  <div className={classes.feeFieldWrap}>
                    {renderField(activeValueField)}
                  </div>
                ) : null}

                {relatedFields.map((field) => (
                  <div className={classes.feeFieldWrap} key={field.backendKey}>
                    {renderField(field)}
                  </div>
                ))}
              </div>
            </Grid>
          );
        })}

        {nonFeeFields.map((field) => renderFieldGrid(field))}
      </Grid>
    );
  };

  const handleSectionClick = (title) =>
    scrollToSection(sectionDomId("loan", title), {
      beforeScroll: () => ensureSectionExpanded(title),
    });

  const hasDuplicateMatches = (fieldKey) => {
    const fieldValue = formData[fieldKey]?.trim() || "";
    if (!fieldValue) {
      return false;
    }
    const results = searchResults[fieldKey] || [];
    return !effectiveEditMode
      ? results.length > 0
      : results.some((res) => String(res["Loan Id"]) !== String(crmId));
  };
  const showAddressLine1Banner =
    !isAddressLine1BannerDismissed && hasDuplicateMatches("AddressLine1");
  const showPostcodeBanner =
    !isPostcodeBannerDismissed && hasDuplicateMatches("Postcode");
  const showKeyPrincipalNameBanner =
    !isKeyPrincipalNameBannerDismissed && hasDuplicateMatches("KeyPrincipalName");

  return (
    <div className={classes.root}>
      <Paper elevation={0} className={classes.formCard}>
        {!isOptionsLoading ? (
          <FormProgressHeader
            sections={sectionStatuses}
            onSectionClick={handleSectionClick}
            progressLabel="Application progress"
            completeLabel="Ready to submit"
          />
        ) : null}
        <div className={classes.fieldsContainer} ref={formCardRef}>
          {availableDraft ? (
            <div className={classes.draftBanner}>
              <div className={classes.draftBannerContent}>
                <Typography className={classes.draftBannerTitle}>
                  Unsaved draft found
                </Typography>
                <Typography className={classes.draftBannerText}>
                  {availableDraft.updatedAt
                    ? `A loan draft saved on ${new Date(
                        availableDraft.updatedAt
                      ).toLocaleString()} is available for your account.`
                    : "A previous loan draft is available for your account."}
                </Typography>
              </div>
              <div className={classes.draftBannerActions}>
                <A_Button
                  label="Start fresh"
                  premiumVariant="secondary"
                  onClick={handleDiscardDraft}
                  disabled={isSubmitting || isOptionsLoading}
                />
                <A_Button
                  label="Resume draft"
                  premiumVariant="primary"
                  onClick={handleRestoreDraft}
                  disabled={isSubmitting || isOptionsLoading}
                />
              </div>
            </div>
          ) : null}
          {(showAddressLine1Banner || showPostcodeBanner || showKeyPrincipalNameBanner) && (
            <div className={classes.stickyBanners}>
              {showAddressLine1Banner && (
                <DuplicateLoanBanner
                  fieldLabel="Address Line 1"
                  onView={(e) => {
                    e.stopPropagation();
                    ensureSectionExpanded("Address Details");
                    focusField("AddressLine1");
                    toggleResultsVisibility("AddressLine1", true);
                  }}
                  onDismiss={() => setIsAddressLine1BannerDismissed(true)}
                />
              )}
              {showPostcodeBanner && (
                <DuplicateLoanBanner
                  fieldLabel="Postcode"
                  onView={(e) => {
                    e.stopPropagation();
                    ensureSectionExpanded("Address Details");
                    focusField("Postcode");
                    toggleResultsVisibility("Postcode", true);
                  }}
                  onDismiss={() => setIsPostcodeBannerDismissed(true)}
                />
              )}
              {showKeyPrincipalNameBanner && (
                <DuplicateLoanBanner
                  fieldLabel="Key Principal Name"
                  onView={(e) => {
                    e.stopPropagation();
                    ensureSectionExpanded("Loan Details");
                    focusField("KeyPrincipalName");
                    toggleResultsVisibility("KeyPrincipalName", true);
                  }}
                  onDismiss={() => setIsKeyPrincipalNameBannerDismissed(true)}
                />
              )}
            </div>
          )}

          <FormErrorSummary
            items={errorItems}
            onItemClick={(item) => {
              ensureSectionExpanded(item.sectionId);
              focusField(item.fieldId);
            }}
          />

          {isOptionsLoading ? (
            <div className={classes.loaderWrap}>
              <CircularProgress size={28} />
              <Typography className={classes.loaderText}>
                Loading data...
              </Typography>
            </div>
          ) : (
            LOAN_STEPS.map((step) => {
              const sectionMeta = SECTION_META[step.title] || {};

              return (
                <Accordion
                  key={step.title}
                  id={sectionDomId("loan", step.title)}
                  expanded={expandedSections.includes(step.title)}
                  onChange={() => handleAccordionToggle(step.title)}
                  className={classes.accordion}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon
                        style={{ color: "var(--color-text-primary)" }}
                      />
                    }
                    className={classes.accordionSummary}
                  >
                    {sectionMeta.icon ? (
                      <div className={classes.summaryIconBox}>
                        {sectionMeta.icon}
                      </div>
                    ) : null}
                    <div className={classes.summaryTextWrap}>
                      <span className={classes.summaryTitle}>{step.title}</span>
                      {sectionMeta.subtitle ? (
                        <span className={classes.summarySubtitle}>
                          {sectionMeta.subtitle}
                        </span>
                      ) : null}
                    </div>
                    <SectionStatusBadge
                      status={statusByTitle[step.title]}
                      lockedTooltip={LOAN_STAGE_LOCK_MESSAGE}
                    />
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    <div className={classes.accordionContent}>
                      {step.title === "Fees"
                        ? renderFeesStep(step)
                        : renderStandardStep(step)}
                    </div>
                  </AccordionDetails>
                </Accordion>
              );
            })
          )}
        </div>

        <div className={classes.actions}>
          <span />
          <div className={classes.actionGroup}>
            {canUseDrafts ? (
              <A_Button
                label={
                  isDraftSaving ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    "Save Draft"
                  )
                }
                premiumVariant="secondary"
                onClick={handleSaveDraft}
                disabled={
                  isSubmitting ||
                  isOptionsLoading ||
                  isDraftSaving ||
                  !isDraftBootstrapComplete
                }
                className={classes.actionButton}
              />
            ) : null}
            {isDeveloperAutofillEnabled ? (
              <A_Button
                label="Random Fill"
                premiumVariant="secondary"
                onClick={handleDeveloperRandomFill}
                disabled={isSubmitting || isOptionsLoading}
                className={classes.actionButton}
              />
            ) : null}
            <Tooltip
              title={
                effectiveEditMode && !isFormDirty
                  ? "No changes to update yet. Edit a field to enable updating."
                  : ""
              }
              arrow
              placement="top"
            >
              <span>
                <A_SecuredAction permission={pageSecurityActions.UPDATE}>
                  <A_Button
                    label={effectiveEditMode ? "Update Loan" : "Submit Loan"}
                    premiumVariant="primary"
                    onClick={handleFinish}
                    disabled={
                      isSubmitting ||
                      isOptionsLoading ||
                      (effectiveEditMode && !isFormDirty)
                    }
                    className={classes.actionButton}
                    startIcon={
                      isSubmitting ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : null
                    }
                  />
                </A_SecuredAction>
              </span>
            </Tooltip>
          </div>
        </div>
      </Paper>
      {showFinishConfirmation && (
        <A_SimpleDialog
          open={showFinishConfirmation}
          title={
            effectiveEditMode
              ? "Confirm Loan Update"
              : "Confirm Loan Submission"
          }
          onClose={() => setShowFinishConfirmation(false)}
          dialogContent={
            effectiveEditMode
              ? "Are you sure you want to save the changes to this loan?"
              : "Are you sure you want to finish and save this loan?"
          }
          maxWidth="xs"
          dialogActions={
            <div className={classes.actionGroup}>
              <A_Button
                label="Cancel"
                premiumVariant="secondary"
                onClick={() => setShowFinishConfirmation(false)}
                disabled={isSubmitting}
              />
              <A_Button
                label={
                  isSubmitting
                    ? effectiveEditMode
                      ? "Updating..."
                      : "Submitting..."
                    : "Confirm"
                }
                premiumVariant="primary"
                onClick={executeSubmission}
                disabled={isSubmitting}
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : null
                }
              />
            </div>
          }
        />
      )}
    </div>
  );
};

export const Add_Loan = React.memo(AddLoanComponent);

AddLoanComponent.propTypes = addLoanPropTypes;
AddLoanComponent.defaultProps = addLoanDefaultProps;

Add_Loan.displayName = "Add_Loan";

Add_Loan.propTypes = addLoanPropTypes;

Add_Loan.defaultProps = addLoanDefaultProps;

const mapStateToProps = (state) => ({
  currentPage: state.currentPage,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    Object.assign({}, toastActions, currentPageActions),
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps, null)(Add_Loan);
